/**
 * Roster import — the pure half (Beta-1 plan P4-16, Tim D07.1 Phase 2). No Firebase, no React.
 *
 *   parseCsv(text)                       → { headers, rows }        (RFC 4180-ish, quotes, CRLF)
 *   mapColumns(headers)                  → { name, firstName, lastName, email, title, site, department, nwt, role } → header index
 *   buildRows(headers, rows, mapping)    → normalized people [{ firstName, lastName, displayName, email, title, siteName, deptName, nwtName, roleText }]
 *   resolveRow(person, org, opts)        → matched units + role + flags (unknown dept, ambiguous team, missing email…)
 *   roleFromTitle(title)                 → stored role key guess
 *   detectDuplicates(resolved, members)  → marks rows already in the org / repeated in the file
 *
 * The AI column mapper (WF7b, P7-08) can replace `mapColumns`; everything else stays.
 */

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/[\s_\-./]+/g, ' ');
const squash = (s) => norm(s).replace(/[^a-z0-9]+/g, '');

export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const src = String(text || '').replace(/^﻿/, '');
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field); field = '';
      if (row.some((v) => v !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((v) => v !== '')) rows.push(row);
  const headers = (rows.shift() || []).map((h) => String(h).trim());
  return { headers, rows };
}

/** Header synonyms → canonical field. First match wins per field. */
const SYNONYMS = {
  email: ['email', 'e mail', 'email address', 'work email', 'mail'],
  firstName: ['first name', 'first', 'given name', 'forename'],
  lastName: ['last name', 'last', 'surname', 'family name'],
  name: ['name', 'full name', 'employee name', 'employee', 'person'],
  title: ['title', 'job title', 'position', 'role title', 'job', 'occupation'],
  site: ['site', 'location', 'facility', 'plant', 'site name', 'work location', 'office'],
  department: ['department', 'dept', 'division', 'unit', 'org unit', 'business unit'],
  nwt: ['nwt', 'natural work team', 'team', 'crew', 'shift', 'work team', 'workgroup', 'work group'],
  role: ['access level', 'role', 'repsit role', 'level', 'access'],
  supervisor: ['supervisor', 'manager', 'reports to', 'reports_to'],
};

export function mapColumns(headers) {
  const mapping = {};
  const used = new Set();
  const normalized = headers.map((h) => norm(h));
  for (const [field, names] of Object.entries(SYNONYMS)) {
    for (const n of names) {
      const idx = normalized.findIndex((h, i) => !used.has(i) && h === n);
      if (idx >= 0) { mapping[field] = idx; used.add(idx); break; }
    }
  }
  // loose second pass ("Dept.", "Work Email", "Job Title (HR)")
  for (const [field, names] of Object.entries(SYNONYMS)) {
    if (mapping[field] != null) continue;
    const idx = normalized.findIndex((h, i) => !used.has(i) && names.some((n) => h.includes(n)));
    if (idx >= 0) { mapping[field] = idx; used.add(idx); }
  }
  return mapping;
}

export function splitFullName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  if (String(name).includes(',')) { // "Sinclair, Dean"
    const [last, first] = String(name).split(',').map((x) => x.trim());
    return { firstName: first || '', lastName: last || '' };
  }
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

export function buildRows(headers, rows, mapping) {
  const get = (r, field) => (mapping[field] != null ? String(r[mapping[field]] ?? '').trim() : '');
  return rows.map((r, i) => {
    let firstName = get(r, 'firstName');
    let lastName = get(r, 'lastName');
    if (!firstName && !lastName) ({ firstName, lastName } = splitFullName(get(r, 'name')));
    return {
      line: i + 2, // 1-based, after the header
      firstName,
      lastName,
      displayName: `${firstName} ${lastName}`.trim(),
      email: get(r, 'email').toLowerCase(),
      title: get(r, 'title'),
      siteName: get(r, 'site'),
      deptName: get(r, 'department'),
      nwtName: get(r, 'nwt'),
      roleText: get(r, 'role'),
      supervisor: get(r, 'supervisor'),
    };
  });
}

/** Role guess from an explicit access-level column, else from the title. */
export function roleFromTitle(roleText, title = '') {
  const r = norm(roleText);
  if (r) {
    if (/(org|organization)\s*admin|administrator/.test(r)) return 'org_admin';
    if (/org\s*it|^it$/.test(r)) return 'org_it';
    if (/exec/.test(r)) return 'executive';
    if (/site|plant|facility/.test(r)) return 'site_manager';
    if (/department|dept|dh/.test(r)) return 'department_lead';
    if (/supervisor|team lead|fls|frontline/.test(r)) return 'team_lead';
    if (/individual|contributor|ic|respondent|member/.test(r)) return 'respondent';
  }
  const t = norm(title);
  if (/\b(ceo|coo|cfo|president|vice president|vp|chief|executive|director)\b/.test(t)) return 'executive';
  if (/\b(plant|site|facility|general)\s+manager\b/.test(t)) return 'site_manager';
  if (/\b(department|dept)\s*(head|manager)\b|\bmanager\b|\bsuperintendent\b/.test(t)) return 'department_lead';
  if (/\b(supervisor|foreman|team lead|team leader|lead)\b/.test(t)) return 'team_lead';
  return 'respondent';
}

function findUnit(list, name, { pickBy = null } = {}) {
  const key = squash(name);
  if (!key) return { match: null, candidates: [] };
  const exact = list.filter((u) => squash(u.name) === key);
  if (exact.length === 1) return { match: exact[0], candidates: exact };
  if (exact.length > 1) {
    const narrowed = pickBy ? exact.filter(pickBy) : exact;
    return narrowed.length === 1 ? { match: narrowed[0], candidates: narrowed } : { match: null, candidates: exact };
  }
  const partial = list.filter((u) => squash(u.name).includes(key) || key.includes(squash(u.name)));
  const narrowed = pickBy ? partial.filter(pickBy) : partial;
  if (narrowed.length === 1) return { match: narrowed[0], candidates: narrowed };
  return { match: null, candidates: partial };
}

/**
 * Match a normalized person against the confirmed structure.
 * @returns {{ ...person, role, siteId, deptId, nwtId, flags: string[], candidates }}
 */
export function resolveRow(person, org, { scopeSiteId = null } = {}) {
  const sites = org?.sites || [];
  const departments = org?.departments || [];
  const teams = org?.teams || [];
  const flags = [];
  const candidates = {};

  const site = findUnit(sites, person.siteName);
  let siteId = site.match?.id || null;
  if (person.siteName && !siteId) { flags.push(site.candidates.length > 1 ? 'ambiguous_site' : 'unknown_site'); candidates.site = site.candidates; }
  if (!person.siteName && sites.length === 1) siteId = sites[0].id;

  const dept = findUnit(departments, person.deptName, { pickBy: siteId ? (d) => d.siteId === siteId : null });
  let deptId = dept.match?.id || null;
  if (person.deptName && !deptId) { flags.push(dept.candidates.length > 1 ? 'ambiguous_department' : 'unknown_department'); candidates.department = dept.candidates; }
  if (deptId && !siteId) siteId = departments.find((d) => d.id === deptId)?.siteId || null;

  const team = findUnit(teams, person.nwtName, { pickBy: deptId ? (t) => t.departmentId === deptId : siteId ? (t) => departments.find((d) => d.id === t.departmentId)?.siteId === siteId : null });
  let nwtId = team.match?.id || null;
  if (person.nwtName && !nwtId) { flags.push(team.candidates.length > 1 ? 'ambiguous_team' : 'unknown_team'); candidates.team = team.candidates; }
  if (nwtId && !deptId) {
    deptId = teams.find((t) => t.id === nwtId)?.departmentId || null;
    if (deptId && !siteId) siteId = departments.find((d) => d.id === deptId)?.siteId || null;
  }

  const role = roleFromTitle(person.roleText, person.title);
  if ((role === 'team_lead' || role === 'respondent') && !nwtId) flags.push('missing_team');
  if (role === 'department_lead' && !deptId) flags.push('missing_department');
  if (role === 'site_manager' && !siteId) flags.push('missing_site');
  if (!person.email) flags.push('missing_email');
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email)) flags.push('bad_email');
  if (!person.firstName && !person.lastName) flags.push('missing_name');
  if (scopeSiteId && siteId && siteId !== scopeSiteId) flags.push('outside_scope');

  return { ...person, role, siteId, deptId, nwtId, flags, candidates };
}

export const FLAG_LABELS = Object.freeze({
  unknown_site: 'Site not in your structure',
  ambiguous_site: 'Several sites match',
  unknown_department: 'Department not in your structure',
  ambiguous_department: 'Several departments match — pick one',
  unknown_team: 'NWT not in your structure',
  ambiguous_team: 'Several NWTs match — pick one',
  missing_team: 'Needs an NWT',
  missing_department: 'Needs a department',
  missing_site: 'Needs a site',
  missing_email: 'No email — cannot create a login',
  bad_email: 'Email looks wrong',
  missing_name: 'No name',
  outside_scope: 'Outside the selected site (skipped)',
  already_member: 'Already a member (skipped)',
  duplicate_in_file: 'Repeated in the file (skipped)',
});

/** Mark rows already in the org (by email) or repeated inside the file. */
export function detectDuplicates(rows, members = []) {
  const existing = new Set((members || []).map((m) => String(m.email || '').toLowerCase()).filter(Boolean));
  const seen = new Set();
  return rows.map((r) => {
    const flags = [...r.flags];
    if (r.email && existing.has(r.email)) flags.push('already_member');
    else if (r.email && seen.has(r.email)) flags.push('duplicate_in_file');
    if (r.email) seen.add(r.email);
    return { ...r, flags };
  });
}

const SKIP = new Set(['already_member', 'duplicate_in_file', 'outside_scope']);
const BLOCK = new Set(['missing_email', 'bad_email', 'missing_name', 'unknown_site', 'ambiguous_site', 'unknown_department', 'ambiguous_department', 'unknown_team', 'ambiguous_team', 'missing_team', 'missing_department', 'missing_site']);

/** 'skip' (not imported, by design) | 'fix' (needs a correction) | 'ready' */
export function rowStatus(row) {
  if (row.flags.some((f) => SKIP.has(f))) return 'skip';
  if (row.flags.some((f) => BLOCK.has(f))) return 'fix';
  return 'ready';
}

/** The CSV template (D07.1): download this, fill it in, upload it. */
export const ROSTER_TEMPLATE_CSV = 'First Name,Last Name,Email,Title,Site,Department,NWT,Access Level\nDean,Sinclair,dean.sinclair@example.com,Auxiliary Operator,Houston Plant,Operations,Shift Crew A,Individual Contributor\n';
